import React from 'react';
import { Assessment } from '../../types';

interface OrderSummaryProps {
  assessment: Assessment;
  selectedRegions: string[];
}

export default function OrderSummary({ assessment, selectedRegions }: OrderSummaryProps) {
  const isPAA = assessment.id === 'pa';
  const numSelectedRegions = selectedRegions.length;
  let extraCharge = 0;
  let basePrice = isPAA ? 399 : assessment.price;
  
  if (isPAA && numSelectedRegions > 1) {
    extraCharge = Math.min((numSelectedRegions - 1) * 150, 600); // Cap at $999 total ($399 + $600)
  } else if (['pia', 'dpia'].includes(assessment.id) && numSelectedRegions > 1) {
    const extraRegions = numSelectedRegions - 1;
    extraCharge = extraRegions * 150;
  }

  const totalPrice = basePrice + extraCharge;

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
      
      <div className="space-y-4">
        <div>
          <h3 className="font-medium text-gray-900">{assessment.name}</h3>
          <p className="text-sm text-gray-500">{assessment.description}</p>
        </div>

        <div className="border-t border-b border-gray-200 py-4 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Base Price</span>
            <div className="text-right">
              <span className="font-medium text-gray-900">${basePrice}</span>
              {isPAA && <span className="block text-sm text-gray-500">(first region)</span>}
            </div>
          </div>

          {(isPAA || ['pia', 'dpia'].includes(assessment.id)) && numSelectedRegions > 0 && (
            <div className="flex justify-between items-baseline">
              <div>
                <span className="text-gray-600">Regions Selected</span>
                <span className="text-sm text-gray-500 block">
                  {numSelectedRegions} {numSelectedRegions === 1 ? 'region' : 'regions'}
                  {numSelectedRegions > 1 && ` (+$${isPAA ? '150' : '150'} per additional region)`}
                </span>
              </div>
              <span className="font-medium text-gray-900">
                {extraCharge > 0 ? `+$${extraCharge}` : '-'}
              </span>
            </div>
          )}
          {isPAA && numSelectedRegions > 1 && (
            <div className="text-sm text-gray-500 pt-2">
              Maximum total price with all regions: $999
            </div>
          )}
        </div>

        <div className="flex justify-between font-medium text-lg">
          <span>Total</span>
          <span>${totalPrice}</span>
        </div>

        <div className="text-sm text-gray-500">
          Estimated completion time: {assessment.estimatedTime}
        </div>
      </div>
    </div>
  );
}